import type { StreamReasoningPart } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

/**
 * Called once per stepId at flush with a merged event whose `message.content`
 * is the join of every chunk pushed for that step. All other metadata is
 * preserved from the LAST event seen.
 */
export type OnMergedReasoningEvent = (mergedEvent: StreamReasoningPart) => void;

export interface ReasoningChunkBuffer {
	/** Buffer a chunk by `stepId`. Returns false (and does NOT buffer) if `stepId` is missing. */
	bufferEvent: (event: StreamReasoningPart) => boolean;
	/** Drain all steps; emit one merged event per step. No-op when empty. */
	flush: () => void;
	/**
	 * Drain only steps where predicate(stepId) returns true. Steps that fail
	 * the predicate are silently dropped. Use during reconnect replay to skip
	 * reasoning for steps that are already completed — those steps don't need
	 * to appear in the research store since their round is done.
	 */
	flushFiltered: (predicate: (stepId: string) => boolean) => void;
	/** Number of steps currently buffered (NOT chunks). */
	readonly size: () => number;
}

/**
 * Per-stepId accumulator for REASONING_PART chunks. Replaces N per-chunk
 * dispatches (each with O(N) string concat in appendStepContent) with one
 * merged dispatch per step (O(N) total). Used during page-load reconnect
 * replay to avoid OOM on large streams.
 */
export const createReasoningChunkBuffer = (
	onMergedEvent: OnMergedReasoningEvent,
): ReasoningChunkBuffer => {
	const buffers = new Map<string, { chunks: string[]; lastEvent: StreamReasoningPart }>();

	const bufferEvent = (event: StreamReasoningPart): boolean => {
		const stepId = event.message.stepId;
		if (!stepId) {
			return false;
		}
		const existing = buffers.get(stepId);
		if (existing) {
			existing.chunks.push(event.message.content);
			existing.lastEvent = event;
		} else {
			buffers.set(stepId, {
				chunks: [event.message.content],
				lastEvent: event,
			});
		}
		return true;
	};

	const flush = () => {
		if (buffers.size === 0) {
			return;
		}
		// Snapshot + clear before invoking the callback so re-entrant flushes are safe.
		const entries = Array.from(buffers.entries());
		buffers.clear();
		for (const [, { chunks, lastEvent }] of entries) {
			onMergedEvent({
				...lastEvent,
				message: {
					...lastEvent.message,
					content: chunks.join(''),
				},
			});
		}
	};

	const flushFiltered = (predicate: (stepId: string) => boolean) => {
		if (buffers.size === 0) {
			return;
		}
		// Snapshot + clear before invoking the callback so re-entrant flushes are safe.
		const entries = Array.from(buffers.entries());
		buffers.clear();

		for (const [stepId, { chunks, lastEvent }] of entries) {
			if (!predicate(stepId)) {
				// Step is already completed — drop its reasoning chunks so it
				// doesn't create an entry in the research store and trigger a
				// spurious "Rovo is thinking" round after reconnect replay.
				continue;
			}
			onMergedEvent({
				...lastEvent,
				message: {
					...lastEvent.message,
					content: chunks.join(''),
				},
			});
		}
	};

	return {
		bufferEvent,
		flush,
		flushFiltered,
		size: () => buffers.size,
	};
};
